import React, { memo, useMemo } from 'react';

import { Box, Badge as UCLBadge } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { Tabs } from 'expo-router';
import { useAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { InteractionManager, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { Typography } from 'components/ui';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import {
  TAB_BAR_MIN_HEIGHT,
  getTabBarItemSafeAreaStyle,
  getTabBarSafeAreaStyle,
} from 'navigation/tab-bar-safe-area';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LANGUAGES, TranslationKey } from 'state/intl/types';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { primitive } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isAndroid, isWeb } from 'utils/environment';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/types';

export const unstable_settings = {
  initialRouteName: 'index',
};

type NavLabelKey = Extract<
  TranslationKey,
  'navHome' | 'navMenu' | 'navMyCode' | 'navOffers' | 'navRewards'
>;

function TabsLayout() {
  // English cold starts already paint correct labels from the static en catalog.
  // Only remount Tabs after async catalogs load for languages where those labels
  // change (e.g. French); otherwise Expo Router would discard early tab state.
  const { locale, language, messagesLoaded } = useLocale();
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();
  const tabsKey = language === LANGUAGES.en ? 'tabs-en' : messagesLoaded ? locale : 'i18n-pending';
  const tabBarStyle = useMemo(
    () => [
      tabBarStyles.tabBarStyle,
      getTabBarSafeAreaStyle({
        isAndroidDevice: isAndroid,
        safeAreaBottomInset,
      }),
    ],
    [safeAreaBottomInset]
  );
  const tabBarItemStyle = useMemo(
    () =>
      getTabBarItemSafeAreaStyle({
        isAndroidDevice: isAndroid,
      }),
    []
  );

  const screenOptions = useMemo(
    () => ({
      lazy: true,
      header: () => <StandardMobileHeader />,
      tabBarButton: NativeTouchFeedbackTabBarIcon,
      tabBarItemStyle,
      tabBarStyle,
      tabBarLabelPosition: 'below-icon' as const,
      sceneStyle: { backgroundColor: primitive.$mayoEggWhite },
    }),
    [tabBarItemStyle, tabBarStyle]
  );

  const navHomeText = t('navHome');
  const navMenuText = t('navMenu');
  const navMyCodeText = t('navMyCode');
  const navOffersText = t('navOffers');
  const navRewardsText = t('navRewards');

  return (
    <Tabs key={tabsKey} screenOptions={screenOptions}>
      <Tabs.Screen
        name="index"
        {...ANALYTICS_HOME}
        options={useMemo(
          () => ({
            header: () => <StandardMobileHeader hideTsm={!isDesktopWeb} />,
            title: navHomeText,
            tabBarButtonTestID: getTestId('Home'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navHome" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="home" focused={focused} />
            ),
          }),
          [isDesktopWeb, navHomeText]
        )}
      />
      <Tabs.Screen
        name="menu"
        {...ANALYTICS_MENU}
        options={useMemo(
          () => ({
            title: navMenuText,
            tabBarButtonTestID: getTestId('Menu'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navMenu" focused={focused} />
            ),
            headerShown: false,
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="menu" focused={focused} />
            ),
          }),
          [navMenuText]
        )}
      />
      <Tabs.Screen
        name="my-code"
        {...ANALYTICS_MY_CODE}
        options={useMemo(
          () => ({
            title: navMyCodeText,
            tabBarButtonTestID: getTestId('My-Code'),
            tabBarItemStyle: [tabBarItemStyle, { display: isDesktopWeb ? 'none' : 'flex' }],
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navMyCode" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="myCode" focused={focused} BadgeComponent={MyCodeBadge} />
            ),
          }),
          [isDesktopWeb, navMyCodeText, tabBarItemStyle]
        )}
      />
      <Tabs.Screen
        name="offers"
        {...ANALYTICS_OFFERS}
        options={useMemo(
          () => ({
            title: navOffersText,
            tabBarButtonTestID: getTestId('Offers'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navOffers" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="offers" focused={focused} />
            ),
            headerShown: false,
          }),
          [navOffersText]
        )}
      />
      <Tabs.Screen
        name="rewards"
        {...ANALYTICS_REWARDS}
        options={useMemo(
          () => ({
            title: navRewardsText,
            tabBarButtonTestID: getTestId('Rewards'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel textKey="navRewards" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="rewards" focused={focused} />
            ),
          }),
          [navRewardsText]
        )}
      />
    </Tabs>
  );
}

export default memo(TabsLayout);

const getTestId = (name: string) => `nav-with-picture-icon-${name}`;

const createAnalyticsEvent = (name: string, route: string, traceName?: TraceName) => ({
  listeners: {
    tabPress: () => {
      if (traceName) {
        startTrace(traceName, { tabPress: 'true' });
      }
      InteractionManager.runAfterInteractions(() => {
        logButtonClick({ attributes: { name, component: ClickEventComponentNames.NAV_BAR } });
      });
    },
  },
});

const ANALYTICS_HOME = createAnalyticsEvent('Home', '/', TraceName.HOME_LOAD);
const ANALYTICS_MENU = createAnalyticsEvent('Menu', '/menu', TraceName.MENU_LOAD);
const ANALYTICS_MY_CODE = createAnalyticsEvent('My Code', '/my-code', TraceName.MY_CODE_LOAD);
const ANALYTICS_OFFERS = createAnalyticsEvent('Offers', '/offers', TraceName.OFFERS_LOAD);
const ANALYTICS_REWARDS = createAnalyticsEvent('Rewards', '/rewards', TraceName.REWARDS_LOAD);

// this doesn't work on web
const NativeTouchFeedbackTabBarIcon = !isWeb
  ? ({ key, ...props }: any) => <TouchableOpacity key={key} activeOpacity={0.3} {...props} />
  : undefined;

const TAB_ICONS = {
  home: {
    base: require('../../assets/images/nav/home.svg'),
    active: require('../../assets/images/nav/home-active.svg'),
  },
  menu: {
    base: require('../../assets/images/nav/menu.svg'),
    active: require('../../assets/images/nav/menu-active.svg'),
  },
  myCode: {
    base: require('../../assets/images/nav/my-code.svg'),
    active: require('../../assets/images/nav/my-code-active.svg'),
  },
  offers: {
    base: require('../../assets/images/nav/offers.svg'),
    active: require('../../assets/images/nav/offers-active.svg'),
  },
  rewards: {
    base: require('../../assets/images/nav/rewards.svg'),
    active: require('../../assets/images/nav/rewards-active.svg'),
  },
};

type TabBarIconProps = {
  name: keyof typeof TAB_ICONS;
  focused: boolean;
  BadgeComponent?: React.ComponentType | undefined;
};

const TabBarIcon: React.FC<TabBarIconProps> = memo(({ name, focused, BadgeComponent }) => {
  const appTakeover = useAppTakeover();
  const appTakeoverMenuIcon =
    name === 'menu'
      ? focused
        ? appTakeover?.menuIconActive ?? appTakeover?.menuIcon
        : appTakeover?.menuIcon
      : undefined;

  return (
    <View style={tabBarStyles.iconWrapper}>
      <View
        style={[tabBarStyles.activeIndicatorContainer, focused && tabBarStyles.activeIndicator]}
      />
      <Image
        alt=""
        aria-hidden={true}
        source={appTakeoverMenuIcon || (focused ? TAB_ICONS[name].active : TAB_ICONS[name].base)}
        style={tabBarStyles.iconStyles}
        {...hiddenAccessibilityPlatformProps}
      />
      {BadgeComponent ? <BadgeComponent /> : null}
    </View>
  );
});

const TabLabel: React.FC<{ textKey: NavLabelKey; focused: boolean }> = memo(
  ({ textKey, focused }) => {
    // Subscribe so English (no Tabs remount) re-reads `t()` after catalog sync.
    const { language, messagesLoaded } = useLocale();
    const text = t(textKey);
    // Keep messagesLoaded in the render graph for non-English before Tabs remounts.
    const labelKey =
      language === LANGUAGES.en ? textKey : `${textKey}-${messagesLoaded ? 'ready' : 'pending'}`;

    return (
      <Typography
        key={labelKey}
        style={focused ? tabBarStyles.focusedLabelStyle : tabBarStyles.unfocusedLabelStyle}
        adjustsFontSizeToFit
        numberOfLines={2}
      >
        {text}
      </Typography>
    );
  }
);

const MyCodeBadge = memo(() => {
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const count = inRestaurantRedemptionCart.reduce(
    (acc, currentValue) => acc + currentValue.quantity,
    0
  );

  if (!count) {
    return null;
  }

  return (
    <BadgeOuter>
      <BadgeInner variant="number-solid">{count.toString()}</BadgeInner>
    </BadgeOuter>
  );
});

const BadgeOuter = Box.withConfig({
  position: 'absolute',
  zIndex: 1,
  top: -5,
  right: 5,
});

const BadgeInner = UCLBadge.withConfig({
  bgColor: primitive.$meltyYellow,
  _text: {
    fontFamily: 'heading',
    fontSize: 'xs',
    color: primitive.$bbqBrown,
  },
  width: '$5',
  height: '$5',
  style: {
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export const tabBarStyles = StyleSheet.create({
  tabBarStyle: {
    backgroundColor: primitive.$mayoEggWhite,
    borderTopColor: '#FFFFFF',
    borderTopWidth: 1,
    minHeight: TAB_BAR_MIN_HEIGHT,
  },
  iconWrapper: {
    alignItems: 'center',
  },
  activeIndicatorContainer: {
    height: 3,
    top: -7,
  },
  activeIndicator: {
    backgroundColor: primitive.$bbqBrown,
    width: 70,
  },
  iconStyles: { height: 22, width: 22 },
  focusedLabelStyle: { fontFamily: 'Flame-Regular', color: primitive.$bbqBrown, fontSize: 12 },
  unfocusedLabelStyle: { fontFamily: 'Flame-RegularSans', color: '#82685B', fontSize: 12 },
});
